import { useCallback } from 'react';
import { isAllowedJobFileUpload } from '@jobos/shared';

import { useToast } from '@/hooks/use-toast';

/**
 * Filter a raw selection down to the types the job library takes, saying how
 * many were dropped.
 *
 * Shared by the desktop drop panel and the mobile capture bar so there is one
 * answer to "what counts as uploadable" and one wording for the refusal. It sits
 * in front of the queue, not inside it, so a rejected file never occupies an
 * upload slot — the division ReceiptDropZone established.
 *
 * The camera path can't produce a reject, but the library picker can: Android
 * pickers happily hand back a video from the same grid as the photos.
 */
export function useAcceptedJobFiles(): (files: File[]) => File[] {
  const { toast } = useToast();

  return useCallback(
    (files: File[]) => {
      const accepted = files.filter((file) =>
        isAllowedJobFileUpload({ contentType: file.type, fileName: file.name }),
      );
      const skipped = files.length - accepted.length;

      if (skipped > 0) {
        toast({
          title: 'Some files skipped',
          description: `${skipped} file${skipped === 1 ? '' : 's'} not supported — images, PDF, Word and Excel only.`,
          variant: 'destructive',
        });
      }

      return accepted;
    },
    [toast],
  );
}
